import { date } from 'quasar'
import { imgixHost } from 'src/config'
import Item from 'src/models/item/Item'
import Model, { ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import ItemPackage from 'src/models/package/ItemPackage'
import ShowingBadge from 'src/models/showing/ShowingBadge'

const { addToDate } = date

export const CONDITIONS = {
  DATE_RANGE: 'date-range',
  DELIVERY_TYPES: 'delivery-types',
  SHOWING_BADGES: 'showing-badges',
  TIME_OF_DAY: 'time-of-day'
}
CONDITIONS.ALL = _.values(CONDITIONS)
Object.freeze(CONDITIONS)

export const CONDITIONS_REQUIREMENTS = {
  // DATE_RANGE
  DATE_RANGE_START: 'dateRangeStart',
  DATE_RANGE_END: 'dateRangeEnd',
  EXCLUDE_DATE_RANGE: 'excludeDateRange',
  // DELIVERY_TYPE
  DELIVERY_TYPES: 'deliveryTypes',
  EXCLUDE_DELIVERY_TYPES: 'excludeDeliveryTypes',
  // SHOWING_BADGES
  SHOWING_BADGES: 'showingBadgeIds',
  MATCH_SHOWING_BADGES: 'matchShowingBadges',
  EXCLUDE_SHOWING_BADGES: 'excludeShowingBadges',
  // TIME_OF_DAY
  TIME_OF_DAY_START: 'timeOfDayStart',
  TIME_OF_DAY_END: 'timeOfDayEnd',
  EXCLUDE_TIME_OF_DAY: 'excludeTimeOfDay'
}
CONDITIONS_REQUIREMENTS.ALL = _.values(CONDITIONS_REQUIREMENTS)
Object.freeze(CONDITIONS_REQUIREMENTS)

export const MATCH_SHOWING_BADGES_BY = {
  ANY: 'any',
  ALL: 'all'
}
MATCH_SHOWING_BADGES_BY.ALL = _.values(MATCH_SHOWING_BADGES_BY)
Object.freeze(MATCH_SHOWING_BADGES_BY)

export default class Category extends Model {
  static name = 'Category'

  static isConfiguration = true
  static deletable = true
  static duplicatable = true
  static versioned = true
  static paginate = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      image: {
        type: String,
        default: null
      },
      availableOnline: {
        type: Boolean,
        default: true
      },
      hasAlcoholAcknowledgement: {
        type: Boolean,
        default: false
      },
      config: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            require_all_showing_badges: false,
            require_showing_badges: false,
            restrict_by_start_date: false,
            restrict_by_end_date: false,
            restrict_by_time: false,
            start_date: '',
            end_date: '',
            start_time: null,
            end_time: null,
            delivery_types: [],
            exclude_delivery_types: true
          }
        }
      },
      conditions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => _(CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          dateRangeStart: null,
          dateRangeEnd: null,
          excludeDateRange: false,
          deliveryTypes: [],
          excludeDeliveryTypes: false,
          matchShowingBadges: MATCH_SHOWING_BADGES_BY.ANY,
          excludeShowingBadges: false,
          timeOfDayStart: null,
          timeOfDayEnd: null,
          excludeTimeOfDay: false
        })
      },
      showingBadges: {
        type: [ShowingBadge],
        reference: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      items: {
        type: [Item],
        output: true,
        reference: true
      },
      itemPackages: {
        type: [ItemPackage],
        output: true,
        reference: true
      },
      libraryParentId: {
        type: ID,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        excludeFromDefault: true
      },
      libraryChildSiteIds: {
        type: [ID],
        output: true,
        excludeFromDefault: true
      },
      libraryDescendantSites: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      }
    }

    this.computed = {
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=190&h=118&fit=crop&auto=format`
      },
      availableNow () {
        if (this.conditions[CONDITIONS.DATE_RANGE]) {
          const dateTime = this.configuration.currentSite.siteTime.getTime()
          if (this.isDateRangeMatch(
            dateTime,
            this.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_START],
            this.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_END],
            this.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DATE_RANGE])
          ) return false
        }

        if (this.conditions[CONDITIONS.TIME_OF_DAY]) {
          const time = this.configuration.currentSite.siteTime
          const hour = time.getHours()
          const minute = time.getMinutes()
          if (this.isTimeOfDayMatch(
            hour,
            minute,
            this.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_START],
            this.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_END],
            this.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_TIME_OF_DAY])
          ) return false
        }

        return true
      },
      hasShowingRestrictions () {
        return this.conditions[CONDITIONS.DATE_RANGE] || this.conditions[CONDITIONS.TIME_OF_DAY] || this.conditions[CONDITIONS.SHOWING_BADGES]
      },
      hasAvailableOnlineItems () {
        return _(this.items.data).some({ active: true, availableOnline: true })
      },
      hasAvailableOnlineItemPackages () {
        return _(this.itemPackages.data).some({ active: true, availableOnline: true })
      },
      hasAvailableOnlineContent () {
        return this.hasAvailableOnlineItems || this.hasAvailableOnlineItemPackages
      },
      hasAvailableOnlineReadyToServeItems () {
        return _(this.items.data).some({ active: true, availableOnline: true, requiresPreparation: false })
      },
      hasAvailableOnlineReadyToServeItemPackages () {
        return _(this.itemPackages.data).some({ active: true, availableOnline: true, requiresPreparation: false })
      },
      hasAvailableOnlineReadyToServeContent () {
        return this.hasAvailableOnlineReadyToServeItems || this.hasAvailableOnlineReadyToServeItemPackages
      }
    }
  }

  isDateRangeMatch (currentTime, dateRangeStart, dateRangeEnd, excludeDateRange) {
    const startDate = new Date(dateRangeStart).getTime()
    const isAfterStartDate = currentTime > startDate

    let isBeforeEndDate = null
    if (dateRangeEnd) {
      const endDate = addToDate(new Date(dateRangeEnd), { days: 1 }).getTime()
      isBeforeEndDate = currentTime < endDate
    }

    const includesDateRange = isAfterStartDate && (_.isNil(isBeforeEndDate) || isBeforeEndDate)

    return excludeDateRange === includesDateRange
  }

  isDeliveryTypeMatch (deliveryTypeOptions, deliveryTypes, excludeDeliveryTypes) {
    const hasAnyDeliveryTypes = _.intersection(deliveryTypes, deliveryTypeOptions).length > 0
    return excludeDeliveryTypes === hasAnyDeliveryTypes
  }

  isShowingMatch (currentShowingBadgeIds, showingBadgeIds, matchShowingBadgesBy, excludeShowingBadges) {
    if (matchShowingBadgesBy === MATCH_SHOWING_BADGES_BY.ANY) {
      // If not all this showing badges are required, then only one of the showing badges needs to be present.
      // If showing badges are required, then the intersection should be above 0. If showings with any badges are excluded, then the intersection must be 0.
      // This is an OR operation.
      const hasAnyShowingBadges = _.intersection(currentShowingBadgeIds, showingBadgeIds).length > 0
      return excludeShowingBadges === hasAnyShowingBadges
    } else {
      // If all this showing badges are required, then all of the showing badges need to be present.
      // If showing badges are required, then the difference should be 0. If showings with all badges are excluded, then the difference must not be 0.
      // This is an AND operation.
      const hasAllShowingBadges = _.difference(currentShowingBadgeIds, showingBadgeIds).length === 0
      return excludeShowingBadges === hasAllShowingBadges
    }
  }

  isTimeOfDayMatch (currentHour, currentMinute, timeOfDayStart, timeOfDayEnd, excludeTimeOfDay) {
    if (_.isNil(timeOfDayStart)) return false

    const [startHour, startMinute] = _.map(timeOfDayStart.split(':'), Number)
    const isAfterStartTime = currentHour > startHour || (currentHour === startHour && currentMinute > startMinute)

    let isBeforeEndTime = null
    if (timeOfDayEnd) {
      const [endHour, endMinute] = _.map(timeOfDayEnd.split(':'), Number)
      isBeforeEndTime = currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)
    }

    const includesTimeOfDay = isAfterStartTime && (_.isNil(isBeforeEndTime) || isBeforeEndTime)

    return excludeTimeOfDay === includesTimeOfDay
  }

  availableForShowings (showings) {
    if (!this.hasShowingRestrictions) return true

    if (!showings || !showings.length) return this.availableNow

    const matchingShowings = _.some(showings, (showing) => {
      return this.availableForShowing(showing)
    })

    return matchingShowings
  }

  availableForShowing (showing) {
    if (!this.hasShowingRestrictions) return true

    if (this.conditions[CONDITIONS.DATE_RANGE]) {
      const dateTime = showing.siteTime.getTime()
      if (this.isDateRangeMatch(
        dateTime,
        this.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_START],
        this.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_END],
        this.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DATE_RANGE])
      ) return false
    }

    if (this.conditions[CONDITIONS.TIME_OF_DAY]) {
      // We want to use the showing time by default
      let time = showing.siteTime
      // If the showing time is in the past, we assume they are ordering in person (i.e. during the showing)
      // In that case, we care if the category is available now, not when the showing started
      if (time.getTime() < this.configuration.currentSite.siteTime.getTime()) {
        time = this.configuration.currentSite.siteTime
      }

      const hour = time.getHours()
      const minute = time.getMinutes()
      if (this.isTimeOfDayMatch(
        hour,
        minute,
        this.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_START],
        this.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_END],
        this.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_TIME_OF_DAY])
      ) return false
    }

    return true
  }

  get requireAllShowingBadges () {
    return this.config.require_all_showing_badges
  }

  set requireAllShowingBadges (value) {
    this.config.require_all_showing_badges = value
  }

  get requireShowingBadges () {
    return this.config.require_showing_badges
  }

  set requireShowingBadges (value) {
    this.config.require_showing_badges = value
  }

  get restrictByStartDate () {
    return this.config.restrict_by_start_date
  }

  set restrictByStartDate (value) {
    this.config.restrict_by_start_date = value
  }

  get restrictByEndDate () {
    return this.config.restrict_by_end_date
  }

  set restrictByEndDate (value) {
    this.config.restrict_by_end_date = value
  }

  get restrictByTime () {
    return this.config.restrict_by_time
  }

  set restrictByTime (value) {
    this.config.restrict_by_time = value
  }

  get startDate () {
    return this.config.start_date
  }

  set startDate (value) {
    this.config.start_date = value
  }

  get endDate () {
    return this.config.end_date
  }

  set endDate (value) {
    this.config.end_date = value
  }

  get startTime () {
    return this.config.start_time
  }

  set startTime (value) {
    this.config.start_time = value
  }

  get endTime () {
    return this.config.end_time
  }

  set endTime (value) {
    this.config.end_time = value
  }
}
